import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { FileUploadZone } from '../pilgrimGuide/upload/FileUploadZone';
import { ValidationErrors } from '../pilgrimGuide/upload/ValidationErrors';
import { WarningMessage } from '../pilgrimGuide/upload/WarningMessage';
import { Download, Copy, Check, Radio } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useGuidesStore } from '../../stores/guidesStore';
import { useOrganizersStore } from '../../stores/organizersStore'; // Import to get organizer data

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const BulkTransferOperatorModal = ({ isOpen, onClose }: Props) => {
  const { translations } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [copiedGuide, setCopiedGuide] = useState<string | null>(null);
  const [availableGuides, setAvailableGuides] = useState<Array<{id: string, name: string}>>([]);
  const { guides } = useGuidesStore();
  const { organizers, fetchOrganizers } = useOrganizersStore(); // Get organizers data
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();

  // Initialize available guides based on user role
  useEffect(() => {
    if (isOpen) {
      // Fetch organizers data if needed
      fetchOrganizers();
    }
  }, [isOpen, fetchOrganizers]);

  useEffect(() => {
    if (userRole === 'organizer' && userId) {
      // Filter guides for this organizer
      const organizerGuides = Object.values(guides).filter(g => g.organizerId === userId);
      setAvailableGuides(organizerGuides.map(g => ({ id: g.id, name: g.name })));
    } else {
      // For admin, show all guides
      setAvailableGuides(Object.values(guides).map(g => ({ id: g.id, name: g.name })));
    }
  }, [guides, userRole, userId]);

  const handleDownloadTemplate = () => {
    const template = [
      {
        passport_number: '123456789',
        new_operator_name: 'Guide Full Name'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-operator-transfer-template.xlsx');
  };

  const handleCopyGuideName = async (guideName: string) => {
    try {
      await navigator.clipboard.writeText(guideName);
      setCopiedGuide(guideName);
      setTimeout(() => setCopiedGuide(null), 2000);
      toast.success(translations?.bulkTransfer?.copySuccess || 'Guide name copied');
    } catch (error) {
      toast.error(translations?.bulkTransfer?.copyError || 'Failed to copy');
    }
  };

  const handleUpload = () => {
    if (!file) return;

    // Read file and validate before navigating
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const data = e.target?.result;
      if (!data) {
        toast.error('Failed to read file data');
        return;
      }

      // Close modal first
      onClose();
      
      // Navigate to status page with file data
      navigate('/bulk-operator-transfer-status', {
        state: { fileData: data },
        replace: true
      });
    };
    
    reader.onerror = () => {
      toast.error('Error reading file');
    };
    
    reader.readAsBinaryString(file);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.bulkOperatorTransfer?.title || "Bulk Transfer Operators"}
    >
      <div className="space-y-6">
        {/* Available Guides - Only show for organizer and admin */}
        {(userRole === 'organizer' || userRole === 'admin') && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-3">
              {translations?.bulkOperatorTransfer?.availableOperators || 'Available Operators'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
              {availableGuides.map((guide) => (
                <motion.div
                  key={guide.id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white p-3 rounded-lg shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Radio className="text-primary-500" size={18} />
                      <span className="font-medium truncate" title={guide.name}>{guide.name}</span>
                    </div>
                    <button
                      onClick={() => handleCopyGuideName(guide.name)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 shrink-0"
                    >
                      {copiedGuide === guide.name ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
              
              {availableGuides.length === 0 && (
                <div className="col-span-2 text-center py-4 text-gray-500">
                  {translations?.bulkOperatorTransfer?.noOperators || 'No operators available'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Template Download */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.bulkOperatorTransfer?.downloadTemplate || 'Download Excel Template'}</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />
        
        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg text-blue-700">
          <h4 className="font-medium mb-2">
            {translations?.bulkOperatorTransfer?.instructions?.title || 'Instructions'}
          </h4>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>
              {translations?.bulkOperatorTransfer?.instructions?.passportRequired || 
                'Passport number is required for each pilgrim'}
            </li>
            <li>
              {translations?.bulkOperatorTransfer?.instructions?.operatorRequired || 
                'New operator name must match exactly (use copy button above)'}
            </li>
            <li>
              {translations?.bulkOperatorTransfer?.instructions?.existingEntry || 
                'Pilgrim must already be in your operation list'}
            </li>
            <li>
              {translations?.bulkOperatorTransfer?.instructions?.sameOrganization || 
                'Operators can only be transferred within the same organization'}
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.bulkOperatorTransfer?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file}
            className={`px-6 py-2 rounded-lg text-white
              ${!file
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {translations?.bulkOperatorTransfer?.upload || 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};